import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { X, Mail, Bell, Clock } from 'lucide-react';

const FollowModal = ({ reviewer, isOpen, onClose, onSuccess }) => {
  const [email, setEmail] = useState('');
  const [consentEmail, setConsentEmail] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation - email is required
    if (!email.trim()) {
      setError('Email is required');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      setError('Please enter a valid email address');
      return;
    }

    if (!consentEmail) {
      setError('You must agree to receive email notifications to follow this reviewer');
      return;
    }

    setLoading(true);

    try {
      const API_URL = '';
      const response = await fetch(`${API_URL}/api/reviewers/${reviewer.id}/follow`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          phone: null,
          consentEmail: true,
          consentSms: false
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to follow reviewer');
      }

      // Success!
      if (onSuccess) {
        onSuccess(data);
      }

      // Close modal
      onClose();

      // Reset form
      setEmail('');
      setConsentEmail(false);
    } catch (err) {
      setError(err.message || 'Failed to follow reviewer. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-md w-full p-8 relative animate-fadeIn">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition z-20"
          disabled={loading}
          data-testid="follow-modal-close"
        >
          <X className="w-6 h-6" />
        </button>

        <div className="mb-6">
          <div className="flex items-center gap-2 mb-2">
            <Bell className="w-6 h-6 text-[#F2D857]" />
            <h2 className="text-2xl font-bold text-black">
              Follow {reviewer?.name}
            </h2>
          </div>
          <p className="text-gray-600">
            Get notified when {reviewer?.name} goes live and is ready to review tracks.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Email Input - Required */}
          <div>
            <label htmlFor="follow-email" className="block text-sm font-medium text-gray-700 mb-2">
              <Mail className="w-4 h-4 inline mr-1" />
              Email Address <span className="text-red-500">*</span>
            </label>
            <Input
              id="follow-email"
              type="email"
              placeholder="your.email@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
              className="w-full"
              required
              data-testid="follow-email-input"
            />
          </div>

          {/* SMS Coming Soon - Disabled */}
          <div className="relative">
            <label className="block text-sm font-medium text-gray-400 mb-2">
              Phone Number (SMS)
            </label>
            <div className="relative">
              <Input
                type="tel"
                placeholder="(555) 123-4567"
                disabled={true}
                className="w-full bg-gray-100 text-gray-400 cursor-not-allowed"
              />
              <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1 bg-amber-100 text-amber-700 text-xs font-medium px-2 py-1 rounded">
                <Clock className="w-3 h-3" />
                Coming Soon
              </div>
            </div>
            <p className="text-xs text-gray-400 mt-1">
              SMS notifications will be available as a PRO feature soon!
            </p>
          </div>

          {/* Email Consent Checkbox */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <label className="flex items-start space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={consentEmail}
                onChange={(e) => setConsentEmail(e.target.checked)}
                disabled={loading}
                className="mt-1 h-4 w-4 text-[#F2D857] border-gray-300 rounded focus:ring-[#F2D857]"
                required
                data-testid="follow-consent-checkbox"
              />
              <span className="text-sm text-gray-700">
                I agree to receive email notifications when {reviewer?.name || 'this reviewer'} goes live on Mic Check Millionaire. 
                You can unsubscribe at any time.
              </span>
            </label>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm" data-testid="follow-error">
              {error}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              onClick={onClose}
              variant="outline"
              className="flex-1"
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="flex-1 bg-[#F2D857] hover:bg-[#F2D857]/90 text-black font-bold"
              disabled={loading || !email.trim() || !consentEmail}
              data-testid="follow-submit-btn"
            >
              {loading ? 'Following...' : 'Follow'}
            </Button>
          </div>
        </form>

        <p className="text-xs text-gray-500 mt-4 text-center">
          Your email will only be used to notify you when {reviewer?.name || 'this reviewer'} goes live.
        </p>
      </div>
    </div>
  );
};

export default FollowModal;
